import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  TextField,
  Typography,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import dayjs from "dayjs";
import Toaster from "../../components/toaster";
import { useAppDispatch, useAppSelector } from "../../helpers/hooks";
import {
  updateAttendance,
} from "../../features/eventDetails/eventRegistrationSlice";
import { getUsersList } from "../../features/eventDetails/eventDetailsSlice";
import Loader from "../../components/loader";
import { EventVolunteerData, User } from "../../helpers/volunteerListMockData";
import SuccessIcon from "../../static/images/success-icon-outlined.svg";
import FailedIcon from "../../static/images/failed_icon.svg";
import { fetchAllEventsList } from "../../redux/slices/allEventslistSlice";
import CustomPagination from "../common/CustomPagination";
import CommonSelect, { OptionGroup } from "../../components/commonSelect";
import ImportIcon from "../../static/images/import_icon.svg";
import * as XLSX from "xlsx";
import { postUpdatedUsersEventHours } from "../eventDetails/updatEventHoursSlice";
import LoadingButton from "@mui/lab/LoadingButton";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface IUpdatedUser {
  userId: number;
  hours: number;
}

export interface EventOption {
  eventId: string;
  eventName: string;
}

export interface IAllEvents {
  active: EventOption[];
  completed: EventOption[];
}

const ITEMS_PER_PAGE = 5;

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pb: 3 }}>{children}</Box>}
    </div>
  );
}

interface props {
  onEventHoursUpdate: any;
  allUserData: EventVolunteerData;
  eventData: any;
  onToast: (type: "success" | "error" | "info", message: string) => void;
}

const ListofVolunteers = ({
  onEventHoursUpdate,
  allUserData,
  eventData,
  onToast,
}: props) => {
  const dispatch = useAppDispatch();
  const [selectedRegisteredUsers, setSelectedRegisteredUsers] = useState<
    User[]
  >([]);
  const [value, setValue] = React.useState(0);
  const [userData, setUserData] = useState<EventVolunteerData | null>(null);
  const [updatedAttendedUserData, setUpdatedAttendedUserData] = useState<
    IUpdatedUser[]
  >([]);
  const [pageNo, setPageNo] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [openToaster, setOpenToaster] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastType, setToastType] = React.useState("");
  const [toastMessage, setToastMessage] = useState("");
  const loggedInUser = useAppSelector((state) => state.userProfile.profile);
  const [emailInputField, setEmailInputField] = useState("");
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setSelectedRow(newValue);
    setPageNo(1);
  };
  const [allEvents, setAllEvents] = useState<IAllEvents | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<string>("");
  const [filters, setFilters] = useState<string[]>([]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await dispatch(fetchAllEventsList("all"));
        // unwrap result
        const data = response.payload as IAllEvents;
        setAllEvents(data);
      } catch (error) {
        console.error("Error fetching token", error);
      }
    };
    fetchEvents();
  }, []);

  const allEventOptions = useMemo((): OptionGroup[] => {
    const groups: OptionGroup[] = [];

    if (allEvents?.active?.length) {
      groups.push({
        header: "Active Events",
        options: allEvents.active.map((event) => ({
          label: event.eventName,
          value: String(event.eventId),
        })),
      });
    }

    if (allEvents?.completed?.length) {
      groups.push({
        header: "Completed Events",
        options: allEvents.completed.map((event) => ({
          label: event.eventName,
          value: String(event.eventId),
        })),
      });
    }

    return groups;
  }, [allEvents]);

  const volunteerDataMap: Record<number, User[]> = {
    0: userData?.registeredUsers ?? [],
    1: userData?.attendedUsers ?? [],
    2: userData?.waitlistedUsers ?? [],
  };

  const tabNames = [
    "Registered Volunteers",
    "Checked-In Volunteers",
    "Reserved Volunteers",
  ];

  const formatDate = (date: any) => {
    let istDate = dayjs(date).add(330, "minute");
    let datestring = dayjs(istDate, "YYYY-MM-DD HH:mm:ss").format(
      "DD MMM, YYYY hh:mm a",
    );
    return datestring;
  };

  /*
    To validate the user's input on type and display toast message if it is not valid
    if valid then add it to updated users with hours 
  */
  const handleUpdateHours = (e: any, listItem: any) => {
    let normalizedValue = parseFloat(e.target.value).toFixed(2);

    if (e.target.value === "0.05") {
      setTimeout(() => {
        onToast("error", "minimum attended hours reached!");
      }, 100);
      return;
    }
    if (isValidInput(e.target.value)) {
      const isExistingUser: IUpdatedUser[] = updatedAttendedUserData?.filter(
        (existingUser: IUpdatedUser) => existingUser.userId === listItem.userId,
      );
      const updatedUser: IUpdatedUser = {
        userId: 0,
        hours: 0,
      };
      //isExistingUser works to find out if the same user is added already to UpdatedAttendedUserData
      if (isExistingUser.length > 0) {
        updatedAttendedUserData?.map((user: any) => {
          if (user.userId === isExistingUser[0]?.userId) {
            user.userId = user.userId;
            user.hours = Number(normalizedValue);
          }
        });
      } else {
        userData?.attendedUsers?.map((user: any) => {
          if (user.userId === listItem?.userId) {
            updatedUser.userId = user.userId;
            updatedUser.hours = Number(normalizedValue);
          }
        });
        setUpdatedAttendedUserData((prevState) => [...prevState, updatedUser]);
      }
    } else {
      setTimeout(() => {
        onToast("error", "minimum attended hours reached!");
      }, 100);
      return;
    }
  };

  /*
    On Update click - To validate the user's updated hours and 
    display toast if it is not valid
    call back function - PUT to update only the updated users with updated hours 
  */
  const onUpdate = () => {
    const isInValidData = updatedAttendedUserData?.filter(
      (updatedUser: IUpdatedUser) =>
        updatedUser.hours === 0.0 || isNaN(updatedUser.hours) === true,
    );
    if (isInValidData.length === 0) {
      const updatedParam = {
        eventId: selectedEvent,
        userHours: updatedAttendedUserData,
      };
      //post updated users with updated hours
      onUpdateEventHours(updatedParam);
    } else {
      setTimeout(() => {
        onToast("error", "Please provide valid attended hour(s)");
      }, 100);
    }
  };

  const onUpdateEventHours = (param: any) => {
      setIsLoading(true);
      dispatch(postUpdatedUsersEventHours(param)).then((res) => {
        if (res.meta.requestStatus == "fulfilled") {
          setIsLoading(false);
          onToast("success", "Updated successfully!");
          handleEventChange(selectedEvent);
        } else {
          setIsLoading(false);
          onToast("error", "Something went wrong!");
        }
      });
    };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allUsers = userData?.registeredUsers || [];
      setSelectedRegisteredUsers(allUsers);
    } else {
      setSelectedRegisteredUsers([]);
    }
  };

  const handleRowSelect = (email: string) => {
    const user = userData?.registeredUsers?.find((u: any) => u.email === email);
    if (!user) return;

    setSelectedRegisteredUsers((prev) => {
      const exists = prev.some((u: any) => u.email === email);
      return exists
        ? prev.filter((u: any) => u.email !== email)
        : [...prev, user];
    });
  };

  const isAllSelected =
    (userData?.registeredUsers?.length ?? 0) > 0 &&
    selectedRegisteredUsers?.length === userData?.registeredUsers?.length;

  const handleMarkAttendance = () => {
    const regIds = selectedRegisteredUsers?.map(
      (user: any) => user.registrationId,
    );
    const payload = {
      registrationIds: regIds,
      markAttendance: true,
      eventId: userData?.eventId,
    };
    setIsLoading(true);
    dispatch(updateAttendance(payload)).then((res: any) => {
      if (res.meta.requestStatus === "fulfilled") {
        onToast("success", "Updated successfully!");
        handleEventChange(selectedEvent);
        setSelectedRegisteredUsers([]);
      } else {
        onToast("error", "Something went wrong!");
      }
      setIsLoading(false);
    });
  };

  const handleUnMarkAttendance = (param: any) => {
    const regIds = selectedRegisteredUsers?.map(
      (user: any) => user.registrationId,
    );
    const payload = {
      registrationIds: regIds,
      markAttendance: false,
      eventId: userData?.eventId,
    };
    setIsLoading(true);
    dispatch(updateAttendance(payload)).then((res) => {
      if (res.meta.requestStatus == "fulfilled") {
        onToast("success", "Updated successfully!");
        handleEventChange(selectedEvent);
        setSelectedRegisteredUsers([]);
      } else {
        onToast("error", "Something went wrong!");
      }

      setIsLoading(false);
    });
  };

  /*
    Validate input value for attended hours
    allow only numbers and not more than 2 decimals
  */
  const isValidInput = (value: any) => {
    const regex = /^(?!.*\..*\.)[0-9]*(\.[0-9]{0,2})?$/;
    return regex.test(value);
  };

  // To close toast message
  const handleToasterClose = () => {
    setOpenToaster(false);
  };

  const handlePageChange = (pageNumber: any, value: any) => {
    setPageNo(value);
  };

  const handleEventChange = async (eventId: string) => {
    setUserData(null);
    setSelectedEvent(eventId);
    setPageNo(1);
    setIsLoading(true);
    try {
      const response: EventVolunteerData[] = await dispatch(
        getUsersList(eventId),
      ).unwrap();
      setUserData(response[0]);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const paginatedRegisteredUsers = userData?.registeredUsers?.slice(
    (pageNo - 1) * ITEMS_PER_PAGE,
    pageNo * ITEMS_PER_PAGE,
  );

  const paginatedAttendedUsers = userData?.attendedUsers?.slice(
    (pageNo - 1) * ITEMS_PER_PAGE,
    pageNo * ITEMS_PER_PAGE,
  );

  const paginatedWaitlistedUsers = userData?.waitlistedUsers?.slice(
    (pageNo - 1) * ITEMS_PER_PAGE,
    pageNo * ITEMS_PER_PAGE,
  );

  const onOptionChange = (value: string) => {
    setSelectedEvent(value);
  };

  const sanitizeSheetName = (name: string) => {
    return name.replace(/[\\\/\?\*\[\]]/g, "_"); // Replace restricted characters with '_'
  };

  const handleDownloadExcel = () => {
    const selectedTabName = tabNames[selectedRow ?? 0];

    const wb = XLSX.utils.book_new(); // Create a new workbook
    const ws1 = XLSX.utils.json_to_sheet(
      volunteerDataMap[selectedRow ?? 0] ?? [],
    );
    XLSX.utils.book_append_sheet(wb, ws1, selectedTabName);
    const selectedGroups = volunteerDataMap[selectedRow ?? 0] ?? [];
    // Iterate through each department and create a sheet
    Object.keys(selectedGroups).length > 0 &&
      Object.entries(selectedGroups).forEach(([shortId, transport]) => {
        // Debug: Log the department name and data
        // Sanitize the department name to ensure it doesn't contain invalid characters
        const sanitizedDeptName = sanitizeSheetName(shortId);
        // Ensure deptData is a valid array of objects
        if (Array.isArray(selectedGroups) && selectedGroups.length > 0) {
          // Convert the department data to a sheet
          const ws = XLSX.utils.json_to_sheet(selectedGroups);
          // Append the sheet to the workbook with the sanitized department name
          XLSX.utils.book_append_sheet(wb, ws, sanitizedDeptName);
        } else {
        }
      });

    // Write the workbook to a file
    XLSX.writeFile(
      wb,
      `list-of-${selectedTabName.replace(" ", "-").toLowerCase()}.xlsx`,
    );
    setToastType("success");
    setToastMessage("File exported successfully");
    setOpenToaster(true);
  };

  return (
    <>
      <Box sx={{ backgroundColor: "#fff", borderRadius: 1, boxShadow: 1 }}>
        {/* HEADER */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#000",
            color: "#fff",
            px: 3,
            py: 2,
          }}
        >
          {/* Title */}
          <Typography variant="h5" fontWeight={400}>
            List of Volunteers
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleDownloadExcel}
              disabled={
                !(selectedEvent && (userData?.registeredUsers.length ?? 0) > 0)
              }
              sx={{
                backgroundColor: "#1976d2",
                textTransform: "none",
                fontWeight: 600,
                height: 43,
                gap: 1,
                "&.Mui-disabled": {
                  backgroundColor: "#bdbdbd",
                  color: "#757575",
                },
              }}
            >
              Export
              <Avatar sx={{ height: 18, width: 18, transform: "rotate(180deg)"}} src={ImportIcon} />
            </Button>
            <CommonSelect
              placeholder="Select Event"
              groupedOptions={allEventOptions}
              value={selectedEvent}
              showSectionHeader
              searchable
              borderColor="#fff"
              onChangeHandler={handleEventChange}
              sx={{
                width: 200,
                borderRadius: "4px",
                backgroundColor: "#f5f5f5",
                "& .MuiOutlinedInput-root": {
                  height: 43,
                },
              }}
            />
          </Box>
        </Box>

        {/* <DialogContent dividers> */}
        {!userData ? (
          <Box py={6} display="flex" flexDirection="column" alignItems="center">
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.secondary" }}
            >
              No users to display
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "text.secondary",
                maxWidth: 400,
                lineHeight: 1.6,
              }}
            >
              Select an event to view users here.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%" }}>
            {/* TABS */}
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs value={value} onChange={handleChange}>
                <Tab
                  sx={{ textTransform: "none", fontSize: "15px", fontWeight: '700' }}
                  label={`Registered Volunteers (${userData?.registeredUsers?.length ?? 0})`}
                />
                <Tab
                  sx={{ textTransform: "none", fontSize: "15px", fontWeight: '700' }}
                  label={`Checked-In Volunteers (${userData?.attendedUsers?.length ?? 0})`}
                />
                <Tab
                  sx={{ textTransform: "none", fontSize: "15px", fontWeight: '700' }}
                  label={`Reserved Volunteers (${userData?.waitlistedUsers?.length ?? 0})`}
                />
              </Tabs>
            </Box>

            {/* TAB 1 */}
            <CustomTabPanel value={value} index={0}>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Box display="flex" alignItems="center">
                          <Checkbox
                            indeterminate={
                              selectedRegisteredUsers?.length > 0 &&
                              selectedRegisteredUsers?.length <
                                (userData?.registeredUsers?.length ?? 0)
                            }
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                          />
                          {/* <Typography fontWeight={600}>All</Typography> */}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Name
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Dept.
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Date
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Time Slot
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Transport
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Location
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Status
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  {isLoading ? (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Loader />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {(paginatedRegisteredUsers?.length ?? 0) === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            No registered user for this event.
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedRegisteredUsers?.map((listItem: User) => (
                          <TableRow key={listItem.email}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedRegisteredUsers?.some(
                                  (u: any) => u.email === listItem.email,
                                )}
                                onChange={() => handleRowSelect(listItem.email)}
                              />
                            </TableCell>
                            <TableCell>{listItem.name}</TableCell>
                            <TableCell>{listItem.department}</TableCell>
                            <TableCell>{formatDate(listItem.date)}</TableCell>
                            <TableCell>{listItem.timeSlot}</TableCell>
                            <TableCell>
                              {listItem.transport?.toLowerCase() === "yes" ? "Yes" : "No"}
                            </TableCell>

                            <TableCell>{listItem.location.length === 0 ? "-" : listItem.location}</TableCell>
                            <TableCell
                              sx={{
                                color: listItem.checkIn ? "green" : "red",
                                fontWeight: 600,
                              }}
                            >
                              {listItem.checkIn ? (
                                <Avatar
                                  sx={{
                                    width: "28%",
                                    height: "28%",
                                  }}
                                  src={SuccessIcon}
                                />
                              ) : (
                                <Avatar
                                  sx={{
                                    width: "28%",
                                    height: "28%",
                                    alignSelf: "center",
                                  }}
                                  src={FailedIcon}
                                  title="user registered, but not attended the event."
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
              {/* ACTION BUTTONS */}
              {userData?.registeredUsers?.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }} />
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="contained"
                      sx={{
                        fontWeight: 700,
                        textTransform: "none",
                      }}
                      onClick={handleMarkAttendance}
                      disabled={selectedRegisteredUsers.length === 0}
                    >
                      Mark Attendance
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      sx={{
                        borderRadius: "2px",
                        borderWidth: 1,
                        color: "#FF0F0F",
                        borderColor: "#FF0F0F",
                        fontWeight: 700,
                        textTransform: "none",
                      }}
                      onClick={handleUnMarkAttendance}
                      disabled={selectedRegisteredUsers.length === 0}
                    >
                      Unmark Attendance
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <CustomPagination
                      totalItems={userData?.registeredUsers?.length ?? 0}
                      currentPage={pageNo}
                      itemsPerPage={ITEMS_PER_PAGE}
                      onPageChange={setPageNo}
                    />
                  </Box>
                </Box>
              )}
            </CustomTabPanel>

            {/* TAB 2 */}
            <CustomTabPanel value={value} index={1}>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Name
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Dept.
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Date
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Transport
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Location
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Attended Hours
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                    <TableBody>
                      {(paginatedAttendedUsers?.length ?? 0) === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No checked-in user for this event.
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedAttendedUsers?.map(
                          (listItem: User, index: number) => (
                            <TableRow
                              key={listItem.name}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell scope="row">{listItem.name}</TableCell>
                              <TableCell scope="row">
                                {listItem.department}
                              </TableCell>
                              <TableCell scope="row">
                                {formatDate(listItem.date)}
                              </TableCell>
                              <TableCell scope="row">
                                {listItem.transport?.toLowerCase() === "yes" ? "Yes" : "No"}
                              </TableCell>
                              <TableCell scope="row">
                                {listItem.location.length === 0 ? "-" : listItem.location}
                              </TableCell>

                              {/* cell for Attended hours */}
                              <TableCell
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                                scope="row"
                              >
                                <TextField
                                  slotProps={{
                                    inputLabel: { shrink: true },
                                    input: {
                                      readOnly:
                                        loggedInUser.userType !== "ADMIN",
                                    },
                                    htmlInput: {
                                      style: { textAlign: "left" },
                                      step: 0.1,
                                      min: 0.01,
                                      inputMode: "decimal",
                                      pattern: "^[0-9]*[.,]?[0-9]*$",
                                    },
                                  }}
                                  sx={{
                                    "& > :not(style)": {
                                      m: 1,
                                      width: "10ch",
                                      height: "4ch",
                                    },
                                    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                                      {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                      },
                                    "& input[type=number]": {
                                      MozAppearance: "textfield",
                                    },
                                  }}
                                  onChange={(e: any) =>
                                    handleUpdateHours(e, listItem)
                                  }
                                  key={`hours-${index}`}
                                  id={`hours-${index}`}
                                  defaultValue={listItem.hours}
                                  type="number"
                                />
                              </TableCell>
                            </TableRow>
                          ),
                        )
                      )}
                    </TableBody>
                </Table>
              </TableContainer>
              {/* Check if the user is admin and add update button */}
              {(userData?.attendedUsers?.length ?? 0) > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }} />
                  {loggedInUser.userType === "ADMIN" && (
                    <LoadingButton variant="contained" onClick={onUpdate} loading={isLoading}>
                      Update
                    </LoadingButton>
                  )}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <CustomPagination
                      totalItems={userData?.attendedUsers?.length ?? 0}
                      currentPage={pageNo}
                      itemsPerPage={ITEMS_PER_PAGE}
                      onPageChange={setPageNo}
                    />
                  </Box>
                </Box>
              )}
            </CustomTabPanel>

            {/* TAB 3 */}
            <CustomTabPanel value={value} index={2}>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Name
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Dept.
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Date
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Time-Slot
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Transport
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Location
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontStyle: "normal", color: "#000" }}>
                          Status
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  {isLoading ? (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Loader />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {(paginatedWaitlistedUsers?.length ?? 0) === 0 ? (
                        <TableRow onClick={() => setSelectedRow(2)}>
                          <TableCell colSpan={6} align="center">
                            No reserved user for this event.
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedWaitlistedUsers?.map(
                          (listItem: User, index: number) => (
                            <TableRow
                              key={listItem.name}
                              onClick={() => setSelectedRow(2)}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell scope="row">{listItem.name}</TableCell>
                              <TableCell scope="row">
                                {listItem.department}
                              </TableCell>
                              <TableCell scope="row">
                                {formatDate(listItem.date)}
                              </TableCell>
                              <TableCell scope="row">
                                {listItem.timeSlot}
                              </TableCell>
                              <TableCell scope="row">
                                {listItem.transport}
                              </TableCell>
                              <TableCell scope="row">
                                {listItem.location.length === 0 ? "-" : listItem.location}
                              </TableCell>
                              <TableCell scope="row">Yes</TableCell>
                            </TableRow>
                          ),
                        )
                      )}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
              {(userData?.waitlistedUsers?.length ?? 0) > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }} />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <CustomPagination
                      totalItems={userData?.waitlistedUsers?.length ?? 0}
                      currentPage={pageNo}
                      itemsPerPage={ITEMS_PER_PAGE}
                      onPageChange={setPageNo}
                    />
                  </Box>
                </Box>
              )}
            </CustomTabPanel>
          </Box>
        )}

        {/* TOASTER */}
        <Toaster
          openToaster={openToaster}
          toastMessage={toastMessage}
          type={toastType}
          handleToasterClose={handleToasterClose}
        />
        {/* </DialogContent> */}
      </Box>
    </>
  );
};
export default React.memo(ListofVolunteers);
